import { useQuery } from '@tanstack/react-query';
import {
  GitlabClient,
  GitlabProvisioningGroupsAndProjectsPage,
} from '../httpClients/GitlabClient';
import { QUERY_KEYS } from './consts';
import { GITLAB_PROVISIONING_PAGE_SIZE } from './useGitlabProvisioningGroupsAndProjectsQuery';

/** PAT + target install id the Manage listing needs (server resolves scope from the id). */
export type GitlabProvisioningInstallationSelectContext = {
  accessToken: string;
  installationId: string;
};

/**
 * Manage variant of the Select listing: same merged, paginated page, but scope is
 * resolved from the install id (path param) instead of a pasted `gitlabUrl` — so
 * Manage needs no connect URL. Stateless: the elevation PAT rides in the body.
 * @param context - PAT + install id; undefined disables the query.
 * @param parentGroupId - Group to list children of; undefined = top level.
 * @param search - Search term; scope-wide flat match across both types when set.
 * @param page - 1-based page number.
 * @param cursor - Opaque deep-paging cursor for this page (undefined on page 1 or an older BE); takes precedence over `page` server-side.
 * @param options - Extra query options (e.g. `enabled`).
 * @returns Query of a single merged page.
 */
export const useGitlabProvisioningInstallationGroupsAndProjectsQuery = (
  context: GitlabProvisioningInstallationSelectContext | undefined,
  parentGroupId: number | undefined,
  search: string,
  page: number,
  cursor: string | undefined,
  options: { enabled?: boolean } = {},
) =>
  useQuery<GitlabProvisioningGroupsAndProjectsPage>({
    // `cursor` is deliberately NOT in the key: it's a deterministic function of
    // (installationId, parentGroupId, search, page), all already keyed, so keying on
    // it too would split one logical page into two cache entries. It rides as a
    // request param only. Safe as long as `page` stays in the key to distinguish each page.
    queryKey: [
      QUERY_KEYS.GITLAB_INSTALLATION_GROUPS_AND_PROJECTS,
      context?.installationId,
      parentGroupId,
      search,
      page,
    ],
    queryFn: () =>
      GitlabClient.getProvisioningInstallationGroupsAndProjects(
        context!.installationId,
        {
          accessToken: context!.accessToken,
          parentGroupId,
          page,
          cursor,
          perPage: GITLAB_PROVISIONING_PAGE_SIZE,
          search: search || undefined,
        },
      ),
    // Live "what can this PAT see" data; the PAT is deliberately not in the
    // queryKey — refetch on mount + evict on unmount so a new PAT can't reuse a
    // prior token's cached scopes.
    staleTime: 0,
    gcTime: 0,
    ...options,
    enabled: (options.enabled ?? true) && context !== undefined,
  });
